import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Pause, Play } from 'lucide-react-native';
import { memo } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { aiColors, tint } from '../../../../../ai-suite/theme';
import { LinePosition, LineVoice } from '../../../functions/useScenePlayback';
import { CustomerAvatar } from './CustomerAvatar';
import { LineWaveform } from './LineWaveform';
import { TypingDots } from './TypingDots';
import { VoiceWave } from './VoiceWave';

/**
 * A customer line on the left: spoken lines carry a play row above their
 * words, its wave moving while the line is heard; a line still coming in
 * shows what has arrived, or typing dots.
 */
export const CustomerBubble = memo(
  ({
    name,
    text,
    turn,
    playing,
    voicing,
    voice,
    position$,
    onPlay,
  }: {
    name: string;
    text: string;
    turn: number;
    playing: boolean;
    /**
     * Being voiced live: its play row stays between sentences, while the
     * next one loads.
     */
    voicing: boolean;
    /** Measured once its clips are known; until then a plain wave. */
    voice: LineVoice | null;
    position$: BehaviorSubject<LinePosition | null>;
    /** Null while the line is still coming in, or with nothing to play. */
    onPlay: ((turn: number) => void) | null;
  }) => {
    const { rpx } = useResponsiveContext();
    // Between a live reply's sentences nothing plays, but it is still
    // being heard: the row holds still instead of flipping back.
    const heard = playing || voicing;
    const Icon = heard ? Pause : Play;
    const wave = (
      <VoiceWave
        color={heard ? aiColors.primary : tint(aiColors.primary, 0.6)}
        active={heard}
      />
    );
    return (
      <View
        style={{ flexDirection: 'row', alignItems: 'flex-end', gap: rpx(8) }}
      >
        <CustomerAvatar size={rpx(32)} />
        <View style={{ maxWidth: '78%', gap: rpx(4) }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: rpx(6),
              paddingHorizontal: rpx(4),
            }}
          >
            <Typography.Text size={rpx(10)} color={aiColors.mutedForeground}>
              {`${name} · 语音`}
            </Typography.Text>
          </View>
          <View
            style={{
              gap: rpx(8),
              borderRadius: rpx(22),
              borderBottomLeftRadius: rpx(6),
              backgroundColor: aiColors.card,
              paddingHorizontal: rpx(14),
              paddingVertical: rpx(10),
              shadowColor: '#0F172A',
              shadowOpacity: 0.05,
              shadowRadius: 3,
              shadowOffset: { width: 0, height: 1 },
              elevation: 1,
            }}
          >
            {onPlay || playing || voicing ? (
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={playing ? '正在播放' : '播放'}
                // Heard live, the line plays on without a tap.
                disabled={!onPlay}
                onPress={() => onPlay?.(turn)}
                hitSlop={6}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: rpx(8),
                  paddingBottom: rpx(8),
                  borderBottomWidth: 1,
                  borderBottomColor: aiColors.border,
                }}
              >
                <Icon
                  size={rpx(14)}
                  color={aiColors.primary}
                  fill={aiColors.primary}
                />
                {voice ? (
                  <LineWaveform
                    voice={voice}
                    turn={turn}
                    playing={heard}
                    position$={position$}
                  />
                ) : (
                  wave
                )}
              </TouchableOpacity>
            ) : null}
            {text ? (
              <Typography.Text
                size={rpx(14)}
                color={aiColors.foreground}
                style={{ lineHeight: rpx(21) }}
              >
                {text}
              </Typography.Text>
            ) : (
              <TypingDots color={aiColors.mutedForeground} />
            )}
          </View>
        </View>
      </View>
    );
  },
);
